import React from 'react';
import type { FlexProps } from '@chakra-ui/react';
import { Box, Container, Flex, Heading, HStack, Text, useBreakpointValue, VStack } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { getCurrentLegalVersionLegalCurrentVersionGetOptions } from '@/client/@tanstack/react-query.gen';
import { AppExternalLink } from '@/components/ui/app-external-link.tsx';
import BundleLogo from '@/icons/BundleLogo.tsx';
import { PRIVACY_POLICY_URL, TOS_URL } from '@/constants/urls.ts';

interface AuthLayoutProps {
	children: React.ReactNode;
	title?: string;
	mt?: FlexProps['mt'];
	pro?: boolean;
	hideLegalLinks?: boolean;
}

export const AuthLayout: React.FC<AuthLayoutProps> = ({ children, title, mt, pro, hideLegalLinks = false }) => {
	const { data: legalVersionData } = useQuery({
		...getCurrentLegalVersionLegalCurrentVersionGetOptions(),
		staleTime: 60 * 60 * 1000, // 1 hour
	});

	const defaultMarginTop = useBreakpointValue({ base: 6, md: 24 });
	const marginTop: FlexProps['mt'] = (mt ?? defaultMarginTop) as FlexProps['mt'];

	const currentLegalVersion = legalVersionData?.current_version;

	return (
		<Container
			maxW="md"
			px={4}
			css={{
				// iOS Safari makes 100vh taller than the visible area (it assumes the
				// address bar is hidden), so the page overflows just enough to scroll,
				// which toggles the address bar and bounces the layout. Cascade to the
				// viewport-accurate unit each browser supports: -webkit-fill-available
				// fixes older iOS (no dvh), 100dvh fixes 15.4+, 100vh is the fallback.
				height: '100vh',
				'@supports (height: -webkit-fill-available)': { height: '-webkit-fill-available' },
				'@supports (height: 100dvh)': { height: '100dvh' },
			}}
		>
			<Flex direction="column" h="100%">
				<Flex mt={marginTop} direction="column" alignItems="center" w="full" h={{ base: '96px', md: '120px' }}>
					<Box asChild mt={2}>
						<BundleLogo pro={pro} />
					</Box>
				</Flex>

				<VStack gap={8} w="full" flex="1">
					{/* Content section */}
					<Box border="none" w="full" bg="surface.canvas" px={{ base: 0, md: 4 }} pb={4} pt={0}>
						{/* Responsive props, not a branch on `useBreakpointValue`: it answers `base` on the first
						    render and reads the media query after, so on a desktop a branch would draw the mobile
						    heading and then replace it with a new element. */}
						{title && (
							<Flex mb={{ base: 6, md: 4 }} align="start" justify="center">
								<Heading size={{ base: '2xl', md: '3xl' }} fontWeight="bold" textAlign="center">
									{title}
								</Heading>
							</Flex>
						)}

						{children}
					</Box>
				</VStack>

				{!hideLegalLinks && (
					<HStack
						mb={{ base: 6, md: 8 }}
						textStyle="body.sm"
						color="fg.muted"
						gap={2}
						align="center"
						justify="center"
						minW="300px"
					>
						<AppExternalLink
							href={currentLegalVersion?.tos_url || TOS_URL}
							color="fg.muted"
							textDecoration="underline"
							_hover={{ color: 'brand.fg' }}
						>
							Terms
						</AppExternalLink>
						<Text>|</Text>
						<AppExternalLink
							href={currentLegalVersion?.privacy_policy_url || PRIVACY_POLICY_URL}
							color="fg.muted"
							textDecoration="underline"
							_hover={{ color: 'brand.fg' }}
						>
							Privacy
						</AppExternalLink>
					</HStack>
				)}
			</Flex>
		</Container>
	);
};
